import { trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick } from "@faire/web-api/events/loggedOutRetailers/click/loggedOutHomeRedesignCta";
import { trackLoggedOutRetailersLoggedOutHomeRedesignView } from "@faire/web-api/events/loggedOutRetailers/view/loggedOutHomeRedesign";
import { ICategoryBrandsCarousel } from "@faire/web-api/indigofair/data/ICategoryBrandsCarousel";
import { isPrerender } from "@faire/web/common/user-agent/isPrerender";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import classNames from "classnames";
import React from "react";

import { SlideIn } from "@faire/retailer/components/SlideIn";
import { setTimeoutIfPossible } from "@faire/retailer/lib/async-functions/setTimeoutIfPossible";
import { handleKeyDown } from "@faire/retailer/lib/handleKeyDown";
import { useSettingHomepageHeroCondensedHeight } from "@faire/retailer/settings/useSettingHomepageHeroCondensedHeight";
import * as animationStyles from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/__internal__/Animation/animation.css";
import { useAnimatedScroll } from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/__internal__/Animation/useAnimatedScroll";
import { invisible } from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/__internal__/Prerender/style.css";
import * as tabStyles from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/__internal__/Tabs/tabs.css";
import { CategoryBestBrandsWithCTA } from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/CategoryBestBrandsBanner/__internal__/CategoryBestBrandsWithCTA";
import * as componentStyles from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/CategoryBestBrandsBanner/styles.css";
import { useCategoryBrandsBannerQuery } from "@faire/retailer/views/home/logged-out-homepage/__internal__/DataFetching/useCategoryBrandsBannerQuery";

export const CategoryBestBrandsBanner: React.FC<{
  loadTimingRef?: React.RefCallback<HTMLElement>;
}> = ({ loadTimingRef }) => {
  const { categoryBestBrands, isFetching } = useCategoryBrandsBannerQuery();

  const isHomepageHeroCondensedHeight = useSettingHomepageHeroCondensedHeight();

  const {
    resumeScroll,
    pauseScroll,
    isVisible,
    currentIndex,
    setCurrentIndex,
  } = useAnimatedScroll(categoryBestBrands?.length ?? 0, 5000);

  const selectedCategory = categoryBestBrands?.[currentIndex];

  const handleClickTab = (index: number, categoryName: string) => {
    trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick(
      "category_best_brands_banner-tab",
      categoryName
    );
    pauseScroll();
    setCurrentIndex(index);
    setTimeoutIfPossible(() => {
      resumeScroll();
    }, 12000);
  };

  const headerRef = useDoActionWhenVisible(() =>
    trackLoggedOutRetailersLoggedOutHomeRedesignView(
      "category_best_brands_banner-header"
    )
  );

  const tabRef = useDoActionWhenVisible(() =>
    trackLoggedOutRetailersLoggedOutHomeRedesignView(
      "category_best_brands_banner-tab"
    )
  );

  if (isFetching) {
    return <div style={{ height: "700px" }}></div>;
  }

  if (!categoryBestBrands?.length || !selectedCategory) {
    return null;
  }

  return (
    <Column className={componentStyles.container} ref={loadTimingRef}>
      <SlideIn
        bottomThreshold={isHomepageHeroCondensedHeight ? 25 : 100}
        offset={getSpacing("5x")}
      >
        <Column
          className={classNames(
            componentStyles.headerAndTabs,
            animationStyles.animatedContainerParent
          )}
        >
          <Typography
            variant={{
              mobileAndAbove: "displaySSerifRegular",
              desktopAndAbove: "displayMSerifRegular",
            }}
            ref={headerRef}
          >
            <StrictLocalMsg
              defaultMessage="The best brands for every shelf—"
              description={{
                text: "category best brands header",
                img: "https://cdn.faire.com/fastly/37eb9f81f6b526bff89f92a2430972f44ff937df777e3cee32069276f75e4fe7.png",
              }}
            />
          </Typography>
          <Typography
            variant={{
              mobileAndAbove: "displaySSerifRegular",
              desktopAndAbove: "displayMSerifRegular",
            }}
          >
            <StrictLocalMsg
              defaultMessage="find them on Faire"
              description={{
                text: "category best brands header",
                img: "https://cdn.faire.com/fastly/37eb9f81f6b526bff89f92a2430972f44ff937df777e3cee32069276f75e4fe7.png",
              }}
            />
          </Typography>
          <Spacer height="2x" />
          <div className={tabStyles.tabBar} ref={tabRef}>
            {categoryBestBrands.map((category, index) => (
              <div
                key={index}
                onClick={() =>
                  handleClickTab(index, category.category_path_identifier ?? "")
                }
                onKeyDown={handleKeyDown(() =>
                  handleClickTab(index, category.category_path_identifier ?? "")
                )}
              >
                <Typography
                  tabIndex={0}
                  data-is-selected={index === currentIndex}
                  className={classNames(tabStyles.tab)}
                >
                  {category.category_name}
                </Typography>
              </div>
            ))}
          </div>
        </Column>

        <div
          style={{ overflow: "hidden", width: "100%" }}
          data-is-visible={isVisible}
          className={animationStyles.animatedContainer}
        >
          {isPrerender() ? (
            <PrerenderCategories categoryBestBrands={categoryBestBrands} />
          ) : (
            <CategoryBestBrandsWithCTA
              selectedCategory={selectedCategory}
              pauseScroll={pauseScroll}
              resumeScroll={resumeScroll}
            />
          )}
        </div>
      </SlideIn>
    </Column>
  );
};

const PrerenderCategories: React.FC<{
  categoryBestBrands: ICategoryBrandsCarousel[];
}> = ({ categoryBestBrands }) => (
  // we want to have all categories in the DOM for prerendering
  <>
    {categoryBestBrands.map((category, index) => (
      <CategoryBestBrandsWithCTA
        key={index}
        selectedCategory={category}
        className={index ? invisible : ""}
      />
    ))}
  </>
);
